"use client"

import { Flame, Target, CheckCircle2, Clock } from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import { surface, sectionLabel } from "@/lib/dashboard-ui"
import { Skeleton } from "@/components/ui/skeleton"
import type { PerformanceOverview } from "@/services/analytics"

interface StatStripProps {
    overview?: PerformanceOverview
    loading?: boolean
}

interface Stat {
    label: string
    value: string
    hint: string
    icon: LucideIcon
    /** Tone is reinforced by the icon and the hint text, never carried by colour alone. */
    tone: "primary" | "neutral"
}

function formatMinutes(total: number): string {
    if (!total) return "0m"
    const hours = Math.floor(total / 60)
    const minutes = total % 60
    if (!hours) return `${minutes}m`
    return minutes ? `${hours}h ${minutes}m` : `${hours}h`
}

/**
 * The four numbers the API already returned and the old dashboard threw away —
 * `currentStreak`, `questionsThisWeek`, `overallAccuracy`, `totalTimeSpentMinutes`.
 * (page.tsx even had a `StatCard` component for them that was never rendered.)
 */
export function StatStrip({ overview, loading }: StatStripProps) {
    const stats: Stat[] = [
        {
            label: "Day streak",
            value: String(overview?.currentStreak ?? 0),
            hint: (overview?.currentStreak ?? 0) > 0 ? "Keep it going" : "Start one today",
            icon: Flame,
            tone: (overview?.currentStreak ?? 0) > 0 ? "primary" : "neutral",
        },
        {
            label: "This week",
            value: String(overview?.questionsThisWeek ?? 0),
            hint: "questions answered",
            icon: Target,
            tone: "neutral",
        },
        {
            label: "Accuracy",
            value: `${Math.round(overview?.overallAccuracy ?? 0)}%`,
            hint: `across ${overview?.totalQuestionsAnswered ?? 0} answered`,
            icon: CheckCircle2,
            tone: "neutral",
        },
        {
            label: "Time studied",
            value: formatMinutes(overview?.totalTimeSpentMinutes ?? 0),
            hint: "all time",
            icon: Clock,
            tone: "neutral",
        },
    ]

    return (
        <section aria-labelledby="stat-strip-heading">
            <h2 id="stat-strip-heading" className={cn(sectionLabel, "mb-3")}>
                Your progress
            </h2>
            <dl className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                {stats.map(({ label, value, hint, icon: Icon, tone }) => (
                    <div key={label} className={cn(surface, "p-4")}>
                        <div className="flex items-start justify-between gap-2">
                            <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
                            <Icon
                                className={cn(
                                    "h-4 w-4 shrink-0",
                                    tone === "primary" ? "text-primary" : "text-muted-foreground"
                                )}
                                aria-hidden="true"
                            />
                        </div>
                        {loading ? (
                            <Skeleton className="mt-2 h-8 w-16" />
                        ) : (
                            <dd className="mt-1 text-3xl font-bold leading-none tracking-tight text-foreground tabular-nums">
                                {value}
                            </dd>
                        )}
                        <p className="mt-1.5 text-xs text-muted-foreground">{hint}</p>
                    </div>
                ))}
            </dl>
        </section>
    )
}
